// Layout of the finished deck's side section (FinishedDeckSectionCard): where every board, groove,
// connector, screw, joist and support is drawn, in the section preview's pixels.
import type { FC, SVGProps } from "react";
import {
  CONNECTOR_FAMILY_CODE_FLAT_FLIP,
  CONNECTOR_FAMILY_CODE_GAP,
  CONNECTOR_FAMILY_CODE_SNAP,
  CONNECTOR_FAMILY_CODE_TERRALOCK,
  CONNECTOR_FAMILY_CODE_TERRALOCK_A2,
  CONNECTOR_FAMILY_CODE_TERRALOCK_PP,
  CONNECTOR_FAMILY_CODE_TVM,
  TERRALOCK_FAMILY_CODES,
} from "../../../http/deckDesigner/types/connectorFamilies";
import { FASTENING_TYPE_CODE_VISIBLE_FASTENING } from "../../../http/deckDesigner/types/fasteningTypes";
import {
  GROOVING_TYPE_CODE_ASYMMETRICAL_GROOVING,
  GROOVING_TYPE_CODE_SYMMETRICAL_GROOVING,
} from "../../../http/deckDesigner/types/groovingTypes";
import { SECONDARY_ITEM_CODE_SNAP } from "../../../http/deckDesigner/types/secondaryItems";
import { SUBSTRUCTURE_MATERIAL_CODE_ALUMINIUM } from "../../../http/deckDesigner/types/substructureMaterials";
import {
  SUPPORT_FAMILY_CODE_GRANULO,
  SUPPORT_FAMILY_CODE_JFA,
  SUPPORT_FAMILY_CODE_NAG,
  SUPPORT_FAMILY_CODE_SUP_L,
  SUPPORT_FAMILY_CODE_SUP_M,
  SUPPORT_FAMILY_CODE_SUP_S,
} from "../../../http/deckDesigner/types/supportFamilies";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import type { Glyph } from "../../../types";
import FlatFlipGraphic, {
  FLAT_FLIP_BASE_STACK_HEIGHT_MM,
  FLAT_FLIP_LOWER_BASE_WIDTH_MM,
  FLAT_FLIP_TOTAL_HEIGHT_MM,
} from "./connectorFamilyGraphics/FlatFlip";
import GapGraphic, {
  GAP_CONNECTOR_ARM_WIDTH_MM,
  GAP_CONNECTOR_BASE_THICKNESS_MM,
  GAP_CONNECTOR_BASE_WIDTH_MM,
  GAP_CONNECTOR_GROOVE_DEPTH_CLEARANCE_MM,
  GAP_CONNECTOR_STACK_TOP_Y_MM,
  GAP_CONNECTOR_TOTAL_HEIGHT_MM,
} from "./connectorFamilyGraphics/Gap";
import { createMillimetersToPixels, getKkScrewLayout } from "./connectorFamilyGraphics/KkScrew";
import {
  SECONDARY_SNAP_BASE_THICKNESS_MM,
  SECONDARY_SNAP_BASE_WIDTH_MM,
  SECONDARY_SNAP_HEAD_SIZE_MM,
  SECONDARY_SNAP_TOTAL_HEIGHT_MM,
} from "./connectorFamilyGraphics/SecondarySnap";
import SnapGraphic, {
  SNAP_CONNECTOR_BASE_THICKNESS_MM,
  SNAP_CONNECTOR_BASE_WIDTH_MM,
  SNAP_CONNECTOR_GROOVE_CLEARANCE_MM,
  SNAP_CONNECTOR_HEAD_SIZE_MM,
} from "./connectorFamilyGraphics/Snap";
import TerralockGraphic from "./connectorFamilyGraphics/Terralock";
import TvmGraphic, {
  TVM_CONNECTOR_ARM_TOP_THICKNESS_MM,
  TVM_CONNECTOR_BASE_GAP_CLEARANCE_MM,
  TVM_CONNECTOR_BASE_WIDTH_MM,
  TVM_CONNECTOR_GROOVE_DEPTH_CLEARANCE_MM,
  TVM_CONNECTOR_TOTAL_HEIGHT_MM,
  TVM_CONNECTOR_WIDTH_MM,
} from "./connectorFamilyGraphics/Tvm";
import { getNonNegativeNumber, getPositiveNumber, isPositiveNumber } from "./deckGeometry";
import GranuloSupportGraphic, {
  GRANULO_SUPPORT_THICKNESS_MM,
  GRANULO_SUPPORT_WIDTH_MM,
} from "./supportFamilyGraphics/Granulo";
import JfaSupportGraphic, { JFA_SUPPORT_WIDTH_MM } from "./supportFamilyGraphics/Jfa";
import NagSupportGraphic, { NAG_SUPPORT_WIDTH_MM } from "./supportFamilyGraphics/Nag";
import SupSupportGraphic, { SUP_SUPPORT_HEIGHT_MM, SUP_SUPPORT_WIDTH_MM } from "./supportFamilyGraphics/Sup";
import type { SectionPreviewSize } from "./ZoomableSectionPreview";

export type SupportGraphicProps = SVGProps<SVGSVGElement> & {
  heightMm: number;
  unitHeightMm?: number;
  widthMm?: number;
};

// The form values the section is drawn from.
export type FinishedDeckSectionValues = Pick<
  DeckDesignerConfigurationFormValues,
  | "totalHeight"
  | "boardWidth"
  | "boardThickness"
  | "groovingHeight"
  | "groovingType"
  | "gap"
  | "fasteningType"
  | "connectorFamily"
  | "connector"
  | "connectorSubstructureScrew"
  | "connectorBoardScrew"
  | "secondaryItem"
  | "substructureMaterial"
  | "substructure"
  | "supportFamily"
  | "support"
>;

type Connector = DeckDesignerConfigurationFormValues["connector"];
type ConnectorScrew = DeckDesignerConfigurationFormValues["connectorSubstructureScrew"];

export type BoardPatternSegment = {
  centerX: number;
  key: string;
  width: number;
  x: number;
};

export type GapPatternSegment = {
  centerX: number;
  key: string;
  leftX: number;
  rightX: number;
};

// A screw drawn standing at centerX (see KkScrew).
export type SectionScrewPoint = {
  centerX: number;
  key: string;
};

// Which dedicated graphic a connector is drawn with in each gap (see FinishedDeckSectionConnectors).
export type SectionConnectorKind = "terralock" | "flatFlip" | "gap" | "tvm" | "snap" | "other";

export type SectionConnectorsLayout = {
  baseHeight: number;
  boardScrew: ConnectorScrew;
  // The family's graphic - without one, the gaps stay empty.
  graphic: Glyph | null;
  graphicHeight: number;
  graphicWidth: number;
  graphicY: number;
  kind: SectionConnectorKind;
  // Only SNAP shows its substructure screw, and only when it's fastened with one.
  snapScrew: ConnectorScrew;
  substructureScrew: ConnectorScrew;
  terralock: {
    height: number;
    postLeftOffset: number;
    postSize: number;
    screwVisibleLengthMm: number;
    topHeight: number;
    width: number;
    y: number;
  };
  tvmTotalHeightMm: number;
};

type SupportGraphicDefinition = {
  Graphic: FC<SupportGraphicProps>;
  fullWidth?: boolean;
  heightMm?: number;
  maxWidthRatio?: number | null;
  widthMm: number;
};

export const SUBSTRUCTURE_BORDER_WIDTH = 1.2;
const TERRALOCK_BASE_WIDTH_MM = 60;
const TERRALOCK_BASE_HEIGHT_MM = 8;
const TERRALOCK_POST_LEFT_OFFSET_MM = 15;
const TERRALOCK_POST_SIZE_MM = 5;
const TERRALOCK_DEFAULT_SCREW_HEIGHT_MM = 30;
const FLAT_FLIP_GROOVE_CLEARANCE_MM = 4;
const SECTION_PREVIEW_BOARD_COUNT = 2;
const MIN_BOARD_SEGMENT_WIDTH_PX = 0.5;
// Shown before the user has picked a fastening/connector - just enough to preview that a groove is
// coming, and roughly matches the real TVM groovingThickness so it doesn't jump once data loads.
const PLACEHOLDER_GROOVE_THICKNESS_MM = 3;
// Fraction of the space below the groove used as the bottom margin, so the placeholder asymmetrical
// groove visibly sits closer to the underside instead of centered like the symmetrical placeholder.
const PLACEHOLDER_ASYMMETRICAL_GROOVE_BOTTOM_RATIO = 0.25;

// Screw connectors (KKT/KKA, see isScrewConnector) have none: they're drawn as screws instead.
const connectorGraphicByFamilyCode: Record<string, Glyph> = {
  [CONNECTOR_FAMILY_CODE_TVM]: TvmGraphic,
  [CONNECTOR_FAMILY_CODE_FLAT_FLIP]: FlatFlipGraphic,
  [CONNECTOR_FAMILY_CODE_GAP]: GapGraphic,
  [CONNECTOR_FAMILY_CODE_SNAP]: SnapGraphic,
  [CONNECTOR_FAMILY_CODE_TERRALOCK]: TerralockGraphic,
  // TODO: swap in the dedicated TERRALOCK A2 / TERRALOCK PP graphics once available - reusing the
  // plain TERRALOCK one as a placeholder since the physical connector shape is otherwise identical.
  [CONNECTOR_FAMILY_CODE_TERRALOCK_A2]: TerralockGraphic,
  [CONNECTOR_FAMILY_CODE_TERRALOCK_PP]: TerralockGraphic,
};

const supportGraphicByFamilyCode: Record<string, SupportGraphicDefinition> = {
  [SUPPORT_FAMILY_CODE_GRANULO]: {
    Graphic: GranuloSupportGraphic,
    fullWidth: true,
    heightMm: GRANULO_SUPPORT_THICKNESS_MM,
    widthMm: GRANULO_SUPPORT_WIDTH_MM,
  },
  [SUPPORT_FAMILY_CODE_JFA]: {
    Graphic: JfaSupportGraphic,
    maxWidthRatio: 0.72,
    widthMm: JFA_SUPPORT_WIDTH_MM,
  },
  [SUPPORT_FAMILY_CODE_NAG]: {
    Graphic: NagSupportGraphic,
    maxWidthRatio: 0.72,
    widthMm: NAG_SUPPORT_WIDTH_MM,
  },
  [SUPPORT_FAMILY_CODE_SUP_S]: {
    Graphic: SupSupportGraphic,
    heightMm: SUP_SUPPORT_HEIGHT_MM,
    maxWidthRatio: null,
    widthMm: SUP_SUPPORT_WIDTH_MM,
  },
  [SUPPORT_FAMILY_CODE_SUP_M]: {
    Graphic: SupSupportGraphic,
    heightMm: SUP_SUPPORT_HEIGHT_MM,
    maxWidthRatio: null,
    widthMm: SUP_SUPPORT_WIDTH_MM,
  },
  [SUPPORT_FAMILY_CODE_SUP_L]: {
    Graphic: SupSupportGraphic,
    heightMm: SUP_SUPPORT_HEIGHT_MM,
    maxWidthRatio: null,
    widthMm: SUP_SUPPORT_WIDTH_MM,
  },
};

const getPixelsPerMillimeter = (availablePixels: number, totalMillimeters: number): number =>
  availablePixels > 0 && totalMillimeters > 0 ? availablePixels / totalMillimeters : 0;

const getSectionPixelsPerMillimeter = (
  availableWidth: number,
  totalWidthMm: number,
  availableHeight: number,
  totalHeightMm: number,
): number => {
  const horizontalPixelsPerMillimeter = getPixelsPerMillimeter(availableWidth, totalWidthMm);
  const verticalPixelsPerMillimeter = getPixelsPerMillimeter(availableHeight, totalHeightMm);

  if (horizontalPixelsPerMillimeter <= 0) {
    return verticalPixelsPerMillimeter;
  }

  if (verticalPixelsPerMillimeter <= 0) {
    return horizontalPixelsPerMillimeter;
  }

  return Math.min(horizontalPixelsPerMillimeter, verticalPixelsPerMillimeter);
};

const getPreviewPatternWidthMm = (boardWidthMm: number, gapMm: number): number =>
  boardWidthMm * SECTION_PREVIEW_BOARD_COUNT + gapMm * Math.max(SECTION_PREVIEW_BOARD_COUNT - 1, 0);

const getRepeatedBoardPattern = ({
  boardWidth,
  centerX,
  endX,
  gapWidth,
  startX,
}: {
  boardWidth: number;
  centerX: number;
  endX: number;
  gapWidth: number;
  startX: number;
}): { boardSegments: BoardPatternSegment[]; gapSegments: GapPatternSegment[] } => {
  const patternStartX = Math.min(startX, endX);
  const patternEndX = Math.max(startX, endX);
  const patternWidth = patternEndX - patternStartX;
  const pitch = boardWidth + gapWidth;

  if (patternWidth <= 0 || boardWidth <= 0 || pitch <= 0) {
    return { boardSegments: [], gapSegments: [] };
  }

  const isGapInsidePattern = (gapCenterX: number): boolean =>
    gapCenterX - gapWidth / 2 > patternStartX && gapCenterX + gapWidth / 2 < patternEndX;
  const gapCenters: number[] = [];

  for (let gapCenterX = centerX; isGapInsidePattern(gapCenterX); gapCenterX -= pitch) {
    gapCenters.push(gapCenterX);
  }

  for (let gapCenterX = centerX + pitch; isGapInsidePattern(gapCenterX); gapCenterX += pitch) {
    gapCenters.push(gapCenterX);
  }

  const gapSegments = gapCenters
    .sort((a, b) => a - b)
    .map((gapCenterX, index) => ({
      centerX: gapCenterX,
      key: `gap-${index}`,
      leftX: gapCenterX - gapWidth / 2,
      rightX: gapCenterX + gapWidth / 2,
    }));
  const boardSegments: BoardPatternSegment[] = [];
  let currentBoardStartX = patternStartX;

  gapSegments.forEach((gapSegment) => {
    const boardWidth = gapSegment.leftX - currentBoardStartX;

    if (boardWidth > MIN_BOARD_SEGMENT_WIDTH_PX) {
      boardSegments.push({
        centerX: currentBoardStartX + boardWidth / 2,
        key: `board-${boardSegments.length}`,
        width: boardWidth,
        x: currentBoardStartX,
      });
    }

    currentBoardStartX = Math.max(currentBoardStartX, gapSegment.rightX);
  });

  const remainingBoardWidth = patternEndX - currentBoardStartX;

  if (remainingBoardWidth > MIN_BOARD_SEGMENT_WIDTH_PX) {
    boardSegments.push({
      centerX: currentBoardStartX + remainingBoardWidth / 2,
      key: `board-${boardSegments.length}`,
      width: remainingBoardWidth,
      x: currentBoardStartX,
    });
  }

  return { boardSegments, gapSegments };
};

// Drawn as screws (KkScrew) instead of a connector in each gap - SNAP never is, even with a screw.
export const isScrewConnector = (connector: Connector): boolean =>
  !!connector &&
  connector.connectorFamily.code !== CONNECTOR_FAMILY_CODE_SNAP &&
  (connector.screw != null ||
    connector.connectorFamily.isScrew === true ||
    connector.connectorFamily.code.toLowerCase().startsWith("kk") ||
    connector.item.code.toLowerCase().startsWith("kk"));

const getSupportPreviewHeight = (
  support: DeckDesignerConfigurationFormValues["support"],
  totalHeightCm: number | null | undefined,
  fixedDeckHeightMm: number,
): number => {
  if (!support) {
    return 0;
  }

  const minHeight = getNonNegativeNumber(support.minHeight, 0);
  const maxHeight = Math.max(minHeight, getNonNegativeNumber(support.maxHeight, minHeight));
  const fallbackHeight = (minHeight + maxHeight) / 2;
  const totalHeightMm = isPositiveNumber(totalHeightCm) ? totalHeightCm * 10 : null;

  if (totalHeightMm == null) {
    return fallbackHeight;
  }

  if (support.supportFamily.code === SUPPORT_FAMILY_CODE_NAG) {
    const supportHeightMm = totalHeightMm - fixedDeckHeightMm;

    if (minHeight <= 0) {
      return 0;
    }

    return Math.max(minHeight, Math.ceil(supportHeightMm / minHeight) * minHeight);
  }

  return Math.min(Math.max(totalHeightMm - fixedDeckHeightMm, minHeight), maxHeight);
};

export const getFinishedDeckSectionLayout = (values: FinishedDeckSectionValues, previewSize: SectionPreviewSize) => {
  const {
    totalHeight,
    boardWidth,
    boardThickness,
    groovingHeight,
    groovingType,
    gap,
    fasteningType,
    connectorFamily,
    connector,
    connectorSubstructureScrew,
    connectorBoardScrew,
    secondaryItem,
    substructureMaterial,
    substructure,
    supportFamily,
    support,
  } = values;
  const boardWidthValue = getPositiveNumber(boardWidth, 140);
  const boardThicknessValue = getPositiveNumber(boardThickness, 24);
  const gapValue = getNonNegativeNumber(gap, 7);
  const substructureHeightValue = getPositiveNumber(substructure?.height, 45);
  const isVisibleFastening = fasteningType?.code === FASTENING_TYPE_CODE_VISIBLE_FASTENING;
  const selectedConnectorFamily = connectorFamily ?? connector?.connectorFamily ?? null;
  const isTvmConnector = selectedConnectorFamily?.code === CONNECTOR_FAMILY_CODE_TVM;
  const isFlatFlipConnector = selectedConnectorFamily?.code === CONNECTOR_FAMILY_CODE_FLAT_FLIP;
  const isGapConnector = selectedConnectorFamily?.code === CONNECTOR_FAMILY_CODE_GAP;
  const isSnapConnector = selectedConnectorFamily?.code === CONNECTOR_FAMILY_CODE_SNAP;
  const selectedSupportFamily = support?.supportFamily ?? supportFamily ?? null;
  const supportGraphicDefinition = selectedSupportFamily
    ? (supportGraphicByFamilyCode[selectedSupportFamily.code] ?? null)
    : null;
  const SupportGraphic = supportGraphicDefinition?.Graphic ?? null;
  const shouldShowSupport = !!support && !!SupportGraphic;
  const isSecondarySnap = secondaryItem?.item.code === SECONDARY_ITEM_CODE_SNAP;
  const connectorAsScrewHeightValue =
    connector?.screw && connector.connectorFamily.code !== CONNECTOR_FAMILY_CODE_SNAP
      ? connector.screw.length + connector.screw.screwHeadHeight
      : 0;
  const visibleFasteningScrewHeightValue =
    isVisibleFastening && connectorSubstructureScrew
      ? connectorSubstructureScrew.length + connectorSubstructureScrew.screwHeadHeight
      : 0;
  const snapConnectorScrewHeightValue =
    isSnapConnector && connector && connectorSubstructureScrew
      ? connectorSubstructureScrew.length + connectorSubstructureScrew.screwHeadHeight
      : 0;
  const longestScrewHeightValue = Math.max(
    connectorAsScrewHeightValue,
    visibleFasteningScrewHeightValue,
    snapConnectorScrewHeightValue,
  );
  const isTerralockConnector = !!connector && TERRALOCK_FAMILY_CODES.includes(connector.connectorFamily.code);
  const hasGapConnector = connector?.connectorFamily.code === CONNECTOR_FAMILY_CODE_GAP;
  const terralockScrewTopHeightValue =
    isTerralockConnector && connectorBoardScrew
      ? Math.max(connectorBoardScrew.length - connectorBoardScrew.screwHeadHeight, 0)
      : null;
  const terralockTopHeightValue = isTerralockConnector
    ? (terralockScrewTopHeightValue ?? TERRALOCK_DEFAULT_SCREW_HEIGHT_MM)
    : 0;

  // The stack's frame: board, connector base, secondary item, substructure and support, scaled to fit.
  const secondaryThickness = isSecondarySnap
    ? SECONDARY_SNAP_BASE_THICKNESS_MM
    : secondaryItem
      ? getPositiveNumber(secondaryItem.thickness, 0)
      : 0;
  const connectorBaseThickness = isTerralockConnector
    ? TERRALOCK_BASE_HEIGHT_MM
    : hasGapConnector
      ? GAP_CONNECTOR_BASE_THICKNESS_MM
      : 0;
  const fixedSupportHeightMm = supportGraphicDefinition?.heightMm;
  const supportHeightMm = shouldShowSupport
    ? (fixedSupportHeightMm ??
      getSupportPreviewHeight(
        support,
        totalHeight,
        boardThicknessValue +
          getNonNegativeNumber(connector?.thickness, 0) +
          secondaryThickness +
          substructureHeightValue,
      ))
    : 0;
  const topOverflow = Math.max(0, terralockTopHeightValue - boardThicknessValue);
  const stackHeightMm =
    topOverflow +
    boardThicknessValue +
    connectorBaseThickness +
    secondaryThickness +
    substructureHeightValue +
    supportHeightMm;
  const screwHeightMm = longestScrewHeightValue > 0 ? boardThicknessValue + longestScrewHeightValue : 0;
  const totalHeightMm = Math.max(stackHeightMm, screwHeightMm);
  const totalWidthMm = getPreviewPatternWidthMm(boardWidthValue, gapValue);
  const horizontalPadding = previewSize.width * (34 / 760);
  const topPadding = previewSize.height * (48 / 400);
  const bottomPadding = previewSize.height * (32 / 400);
  const availableWidth = previewSize.width - horizontalPadding * 2;
  const availableHeight = previewSize.height - topPadding - bottomPadding;
  const pixelsPerMillimeter = getSectionPixelsPerMillimeter(
    availableWidth,
    totalWidthMm,
    availableHeight,
    totalHeightMm,
  );
  const toPixels = createMillimetersToPixels(pixelsPerMillimeter);
  const boardTop = topPadding + toPixels(topOverflow);
  const boardHeight = toPixels(boardThicknessValue);
  const gapWidth = toPixels(gapValue);
  const boardWidthPx = toPixels(boardWidthValue);
  const boardBottom = boardTop + boardHeight;
  const connectorBaseHeight = toPixels(connectorBaseThickness);
  const secondaryHeight = toPixels(secondaryThickness);
  const secondaryY = boardBottom + connectorBaseHeight;
  const substructureY = secondaryItem ? secondaryY + secondaryHeight : boardBottom + connectorBaseHeight;
  const substructureHeight = toPixels(substructureHeightValue);
  const substructureWidth = toPixels(totalWidthMm);
  const substructureX = (previewSize.width - substructureWidth) / 2;
  const { boardSegments, gapSegments } = getRepeatedBoardPattern({
    boardWidth: boardWidthPx,
    centerX: substructureX + substructureWidth / 2,
    endX: substructureX + substructureWidth,
    gapWidth,
    startX: substructureX,
  });
  const supportHeight = toPixels(supportHeightMm);
  const supportY = substructureY + substructureHeight;

  const isConnectorScrew = isScrewConnector(connector);
  const shouldShowConnectorAsScrews = !!connector && isConnectorScrew;
  const shouldShowVisibleFasteningScrews = !!connector && !isConnectorScrew && isVisibleFastening;
  const shouldAlignConnectorScrewsWithSecondarySnap = isSecondarySnap && shouldShowConnectorAsScrews;
  const shouldShowSnapConnectorScrew =
    !!connector && isSnapConnector && !isConnectorScrew && !isVisibleFastening && connectorSubstructureScrew != null;
  const selectedSubstructureMaterialCode =
    substructureMaterial?.code ??
    (substructure?.substructureMaterials.some((m) => m.code === SUBSTRUCTURE_MATERIAL_CODE_ALUMINIUM)
      ? SUBSTRUCTURE_MATERIAL_CODE_ALUMINIUM
      : (substructure?.substructureMaterials[0]?.code ?? null));
  const isAluminiumSubstructure = selectedSubstructureMaterialCode === SUBSTRUCTURE_MATERIAL_CODE_ALUMINIUM;
  const isSymmetricalGrooving = groovingType?.code === GROOVING_TYPE_CODE_SYMMETRICAL_GROOVING;
  const isAsymmetricalGrooving = groovingType?.code === GROOVING_TYPE_CODE_ASYMMETRICAL_GROOVING;
  const grooveThickness = selectedConnectorFamily?.groovingThickness ?? null;
  const grooveBottomHeight = groovingHeight;
  // Once the user picks a grooving type, show a groove right away as a preview - using the real
  // thickness/position once known, otherwise a placeholder that still respects symmetrical vs
  // asymmetrical so the shown example matches what will eventually be calculated.
  const hasGroove = isSymmetricalGrooving || isAsymmetricalGrooving;
  const grooveThicknessValue = getPositiveNumber(grooveThickness, PLACEHOLDER_GROOVE_THICKNESS_MM);
  const grooveBottomHeightValue = getNonNegativeNumber(
    grooveBottomHeight,
    isSymmetricalGrooving
      ? (boardThicknessValue - grooveThicknessValue) / 2
      : (boardThicknessValue - grooveThicknessValue) * PLACEHOLDER_ASYMMETRICAL_GROOVE_BOTTOM_RATIO,
  );
  const grooveHeight = hasGroove ? Math.min(toPixels(grooveThicknessValue), boardHeight * 0.7) : 0;
  const flatFlipMinimumGrooveSpan = toPixels(FLAT_FLIP_LOWER_BASE_WIDTH_MM + FLAT_FLIP_GROOVE_CLEARANCE_MM);
  const flatFlipMinimumGrooveDepth = isFlatFlipConnector ? Math.max(0, (flatFlipMinimumGrooveSpan - gapWidth) / 2) : 0;
  const gapMinimumGrooveDepth = isGapConnector
    ? toPixels(GAP_CONNECTOR_ARM_WIDTH_MM + GAP_CONNECTOR_GROOVE_DEPTH_CLEARANCE_MM)
    : 0;
  const tvmLowerGapWidthValue = Math.max(gapValue, TVM_CONNECTOR_BASE_WIDTH_MM + TVM_CONNECTOR_BASE_GAP_CLEARANCE_MM);
  const tvmArmInsertionDepthValue = Math.max(0, (TVM_CONNECTOR_WIDTH_MM - TVM_CONNECTOR_BASE_WIDTH_MM) / 2);
  const tvmMinimumGrooveDepth = isTvmConnector
    ? toPixels(tvmArmInsertionDepthValue + TVM_CONNECTOR_GROOVE_DEPTH_CLEARANCE_MM)
    : 0;
  const snapMinimumGrooveSpan = toPixels(SNAP_CONNECTOR_BASE_WIDTH_MM + SNAP_CONNECTOR_GROOVE_CLEARANCE_MM * 2);
  const snapMinimumGrooveDepth = isSnapConnector ? Math.max(0, (snapMinimumGrooveSpan - gapWidth) / 2) : 0;
  const defaultGrooveDepth = hasGroove ? Math.min(grooveHeight * 1.8, boardWidthPx * 0.25) : 0;
  const grooveDepth = hasGroove
    ? Math.min(
        Math.max(
          defaultGrooveDepth,
          flatFlipMinimumGrooveDepth,
          gapMinimumGrooveDepth,
          tvmMinimumGrooveDepth,
          snapMinimumGrooveDepth,
        ),
        boardWidthPx / 2,
      )
    : 0;
  const grooveY = hasGroove
    ? Math.min(
        Math.max(boardBottom - toPixels(grooveBottomHeightValue) - grooveHeight, boardTop),
        boardBottom - grooveHeight,
      )
    : boardBottom;
  const connectorThickness = toPixels(getPositiveNumber(connector?.thickness, 5));
  const connectorPlateHeight = Math.min(Math.max(connectorThickness, toPixels(1.5)), boardHeight * 0.5);
  const connectorPlateWidth = Math.min(gapWidth + grooveDepth * 2 + connectorPlateHeight * 8, boardWidthPx + gapWidth);
  // The connector's base foot reaches minGroovingHeight below the bottom of the groove - it varies
  // per connector, unlike the rest of the stamped profile, so the graphic's total height (and with it
  // the outer box height below) is derived instead of using the fixed TVM_CONNECTOR_TOTAL_HEIGHT_MM.
  const tvmMinGroovingHeightValue = getNonNegativeNumber(
    connector?.minGroovingHeight ?? selectedConnectorFamily?.groovingMinHeight,
    TVM_CONNECTOR_TOTAL_HEIGHT_MM - TVM_CONNECTOR_ARM_TOP_THICKNESS_MM,
  );
  const tvmTotalHeightValue =
    isTvmConnector && hasGroove && grooveThickness != null
      ? (grooveThickness + TVM_CONNECTOR_ARM_TOP_THICKNESS_MM) / 2 + tvmMinGroovingHeightValue
      : TVM_CONNECTOR_TOTAL_HEIGHT_MM;
  const connectorGraphicHeight = isFlatFlipConnector
    ? toPixels(FLAT_FLIP_TOTAL_HEIGHT_MM)
    : isGapConnector
      ? toPixels(GAP_CONNECTOR_TOTAL_HEIGHT_MM)
      : isTvmConnector
        ? toPixels(tvmTotalHeightValue)
        : isSnapConnector
          ? toPixels(SNAP_CONNECTOR_BASE_THICKNESS_MM + SNAP_CONNECTOR_HEAD_SIZE_MM)
          : connectorPlateHeight * 4;
  const connectorGraphicWidth = isFlatFlipConnector
    ? toPixels(FLAT_FLIP_LOWER_BASE_WIDTH_MM)
    : isGapConnector
      ? toPixels(GAP_CONNECTOR_BASE_WIDTH_MM)
      : isTvmConnector
        ? toPixels(TVM_CONNECTOR_WIDTH_MM)
        : isSnapConnector
          ? toPixels(SNAP_CONNECTOR_BASE_WIDTH_MM)
          : Math.min(connectorPlateWidth * 0.65, gapWidth + connectorPlateHeight * 7);
  const connectorAssemblyY = boardBottom - grooveHeight - connectorPlateHeight * 0.5;
  const flatFlipBaseStackHeight = toPixels(FLAT_FLIP_BASE_STACK_HEIGHT_MM);
  const gapStackTopOffset = toPixels(GAP_CONNECTOR_STACK_TOP_Y_MM);
  const tvmArmTopHeight = toPixels(TVM_CONNECTOR_ARM_TOP_THICKNESS_MM);
  const snapConnectorBaseHeight = toPixels(SNAP_CONNECTOR_BASE_THICKNESS_MM);
  const connectorGraphicY =
    isFlatFlipConnector && hasGroove
      ? grooveY + (grooveHeight - flatFlipBaseStackHeight) / 2
      : isGapConnector
        ? boardBottom - gapStackTopOffset
        : isTvmConnector && hasGroove
          ? grooveY + (grooveHeight - tvmArmTopHeight) / 2
          : isTvmConnector
            ? boardBottom - tvmArmTopHeight
            : isSnapConnector && hasGroove
              ? grooveY + (grooveHeight - snapConnectorBaseHeight) / 2
              : isSnapConnector
                ? boardBottom - snapConnectorBaseHeight
                : connectorAssemblyY - connectorGraphicHeight * 0.75;
  const terralockTopHeight = toPixels(terralockTopHeightValue);
  const connectorAsScrewLayout = getKkScrewLayout({
    screw: shouldShowVisibleFasteningScrews ? (connectorSubstructureScrew ?? connector?.screw) : connector?.screw,
    toPixels,
    centerX: 0,
    substructureMaterialCode: selectedSubstructureMaterialCode,
    anchor: { type: "headBottom", y: boardTop },
  });
  const tvmLowerGapWidth = toPixels(tvmLowerGapWidthValue);
  const visibleScrewEdgeInset = Math.min(
    boardWidthPx / 2,
    Math.max(connectorAsScrewLayout.width / 2 + toPixels(2), toPixels(10)),
  );
  const secondarySnapWidth = toPixels(SECONDARY_SNAP_BASE_WIDTH_MM);
  const secondarySnapSegments = gapSegments.map((gapSegment) => ({
    centerX: gapSegment.centerX,
    key: gapSegment.key,
    leftX: gapSegment.centerX - secondarySnapWidth / 2,
  }));
  const visibleScrewPoints: SectionScrewPoint[] = gapSegments.flatMap((gapSegment) => [
    {
      centerX: gapSegment.leftX - visibleScrewEdgeInset,
      key: `${gapSegment.key}-left`,
    },
    {
      centerX: gapSegment.rightX + visibleScrewEdgeInset,
      key: `${gapSegment.key}-right`,
    },
  ]);
  const screwConnectorPoints: SectionScrewPoint[] = shouldAlignConnectorScrewsWithSecondarySnap
    ? secondarySnapSegments.flatMap((snapSegment) => [
        {
          centerX: snapSegment.leftX + secondarySnapWidth * 0.25,
          key: `${snapSegment.key}-snap-left`,
        },
        {
          centerX: snapSegment.leftX + secondarySnapWidth * 0.75,
          key: `${snapSegment.key}-snap-right`,
        },
      ])
    : boardSegments.map((boardSegment) => ({
        centerX: boardSegment.centerX,
        key: boardSegment.key,
      }));
  const supportGraphicMaxWidth =
    supportGraphicDefinition?.maxWidthRatio == null
      ? Number.POSITIVE_INFINITY
      : substructureWidth * supportGraphicDefinition.maxWidthRatio;
  const supportGraphicWidth =
    shouldShowSupport && supportGraphicDefinition
      ? supportGraphicDefinition.fullWidth
        ? substructureWidth
        : Math.min(toPixels(supportGraphicDefinition.widthMm), supportGraphicMaxWidth)
      : 0;
  const supportPixelsPerMillimeter = toPixels(1);
  const connectorKind: SectionConnectorKind = isTerralockConnector
    ? "terralock"
    : isFlatFlipConnector
      ? "flatFlip"
      : isGapConnector
        ? "gap"
        : isTvmConnector
          ? "tvm"
          : isSnapConnector
            ? "snap"
            : "other";

  return {
    boards: {
      bottom: boardBottom,
      height: boardHeight,
      segments: boardSegments,
      top: boardTop,
    },
    // One connector per gap, unless it's fastened with screws (visible fastening or a screw connector).
    connectors:
      !isVisibleFastening && !isConnectorScrew && connector
        ? ({
            baseHeight: connectorBaseHeight,
            boardScrew: connectorBoardScrew,
            graphic: connectorGraphicByFamilyCode[connector.connectorFamily.code] ?? null,
            graphicHeight: connectorGraphicHeight,
            graphicWidth: connectorGraphicWidth,
            graphicY: connectorGraphicY,
            kind: connectorKind,
            snapScrew: shouldShowSnapConnectorScrew ? connectorSubstructureScrew : null,
            substructureScrew: connectorSubstructureScrew,
            terralock: {
              height: terralockTopHeight + connectorBaseHeight,
              postLeftOffset: toPixels(TERRALOCK_POST_LEFT_OFFSET_MM),
              postSize: toPixels(TERRALOCK_POST_SIZE_MM),
              screwVisibleLengthMm: terralockTopHeightValue,
              topHeight: terralockTopHeight,
              width: toPixels(TERRALOCK_BASE_WIDTH_MM),
              y: boardBottom - terralockTopHeight,
            },
            tvmTotalHeightMm: tvmTotalHeightValue,
          } satisfies SectionConnectorsLayout)
        : null,
    gapSegments,
    groove: {
      depth: grooveDepth,
      height: grooveHeight,
      isVisible: hasGroove,
      y: grooveY,
    },
    screws: {
      connectorScrew: connector?.screw,
      // A screw connector: one screw per board (or two per SNAP secondary profile).
      connectorScrewPoints: shouldShowConnectorAsScrews ? screwConnectorPoints : null,
      substructureMaterialCode: selectedSubstructureMaterialCode,
      visibleFasteningScrew: connectorSubstructureScrew ?? connector?.screw,
      // Visible fastening: two screws through each board's face, one on each side of every gap.
      visibleFasteningScrewPoints: shouldShowVisibleFasteningScrews ? visibleScrewPoints : null,
    },
    secondary: {
      height: secondaryHeight,
      // The SNAP secondary profile is drawn per gap, any other secondary item as a strip.
      isSnap: isSecondarySnap,
      item: secondaryItem,
      snapHeadHeight: toPixels(SECONDARY_SNAP_HEAD_SIZE_MM),
      snapHeight: toPixels(SECONDARY_SNAP_TOTAL_HEIGHT_MM),
      snapSegments: secondarySnapSegments,
      snapWidth: secondarySnapWidth,
      y: secondaryY,
    },
    substructure: {
      borderHeight: Math.max(0, substructureHeight - SUBSTRUCTURE_BORDER_WIDTH),
      borderInset: SUBSTRUCTURE_BORDER_WIDTH / 2,
      borderWidth: Math.max(0, substructureWidth - SUBSTRUCTURE_BORDER_WIDTH),
      height: substructureHeight,
      isAluminium: isAluminiumSubstructure,
      width: substructureWidth,
      x: substructureX,
      y: substructureY,
    },
    support:
      shouldShowSupport && supportHeight > 0 && supportGraphicWidth > 0
        ? {
            Graphic: SupportGraphic,
            height: supportHeight,
            heightMm: supportHeightMm,
            isFullWidth: !!supportGraphicDefinition?.fullWidth,
            // NAG is a stack of layers, each as tall as its minimum height.
            familyProps:
              selectedSupportFamily?.code === SUPPORT_FAMILY_CODE_NAG ? { unitHeightMm: support?.minHeight } : {},
            width: supportGraphicWidth,
            widthMm:
              supportPixelsPerMillimeter > 0 && Number.isFinite(supportPixelsPerMillimeter)
                ? supportGraphicWidth / supportPixelsPerMillimeter
                : (supportGraphicDefinition?.widthMm ?? 0),
            x: supportGraphicDefinition?.fullWidth
              ? substructureX
              : substructureX + (substructureWidth - supportGraphicWidth) / 2,
            y: supportY,
          }
        : null,
    toPixels,
    tvmLowerGap: {
      isVisible: isTvmConnector && hasGroove && tvmLowerGapWidth > gapWidth,
      width: tvmLowerGapWidth,
      y: grooveY + grooveHeight,
    },
  };
};

export type FinishedDeckSectionLayout = ReturnType<typeof getFinishedDeckSectionLayout>;
